import { useEffect, useState } from 'react';
import { CountryRow, DataMode } from '../types/census';
import { getCountries, getIndicatorAll, INDICATORS } from '../utils/api';
import { deriveEstimates } from '../utils/estimates';
import { medianAge } from '../data/medianAge';

export interface CountryDataState {
  loading: boolean;
  rows: CountryRow[];
  modes: { countries: DataMode; internet: DataMode; mobile: DataMode; working: DataMode };
  asOf: { countries: string; internet: string; mobile: string; working: string };
}

let cached: CountryDataState | null = null;
let pending: Promise<CountryDataState> | null = null;

const EMPTY: CountryDataState = {
  loading: true,
  rows: [],
  modes: { countries: 'snapshot', internet: 'snapshot', mobile: 'snapshot', working: 'snapshot' },
  asOf: { countries: '', internet: '', mobile: '', working: '' },
};

export function loadCountryData(): Promise<CountryDataState> {
  if (!pending) {
    pending = Promise.all([
      getCountries(),
      getIndicatorAll(INDICATORS.internet),
      getIndicatorAll(INDICATORS.mobile),
      getIndicatorAll(INDICATORS.working),
    ]).then(([countries, internet, mobile, working]) => {
      const rows: CountryRow[] = countries.rows.map((b) => {
        const i = internet.values[b.cca3] ?? null;
        const m = mobile.values[b.cca3] ?? null;
        const w = working.values[b.cca3] ?? null;
        const est = deriveEstimates(b.population, i?.value ?? null, m?.value ?? null);
        return { ...b, internetPct: i, mobilePer100: m, workingAgePct: w, medianAge: medianAge[b.cca3] ?? null, ...est };
      });
      cached = {
        loading: false,
        rows,
        modes: { countries: countries.mode, internet: internet.mode, mobile: mobile.mode, working: working.mode },
        asOf: { countries: countries.asOf, internet: internet.asOf, mobile: mobile.asOf, working: working.asOf },
      };
      return cached;
    });
  }
  return pending;
}

export function useCountryData(): CountryDataState {
  const [state, setState] = useState<CountryDataState>(cached ?? EMPTY);
  useEffect(() => {
    if (cached) return;
    let alive = true;
    loadCountryData().then((s) => {
      if (alive) setState(s);
    });
    return () => {
      alive = false;
    };
  }, []);
  return state;
}
